import { compareInboxKeysetDesc } from "./conversation-inbox-keyset.ts";

/** Ids promovidos por rajada de realtime, numa única query `.in("id", ...)`. */
export const INBOX_PROMOTION_BATCH_LIMIT = 20;

/** Teto da reconciliação por `updated_at` ao voltar para a aba. */
export const INBOX_DELTA_FETCH_LIMIT = 50;

export interface InboxSortCursor {
  id: string;
  /**
   * `undefined` = o payload não trouxe a coluna.
   * `null` = a coluna veio nula (ordena no fim da inbox).
   */
  lastMessageAt: string | null | undefined;
}

export interface InboxWindowSnapshot {
  loadedCount: number;
  pageSize: number;
  hasMore: boolean;
  tail: { id: string; lastMessageAt: string | null } | null;
}

/**
 * Lê o cursor de ordenação de um payload de `conversations`.
 * Não usa o restante da linha para renderizar — só para decidir se vale buscar.
 */
export function readInboxSortCursor(row: Record<string, unknown>): InboxSortCursor | null {
  const id = typeof row.id === "string" ? row.id : null;
  if (!id) return null;

  if (!Object.prototype.hasOwnProperty.call(row, "last_message_at")) {
    return { id, lastMessageAt: undefined };
  }

  const value = row.last_message_at;
  return { id, lastMessageAt: typeof value === "string" ? value : null };
}

export function inboxTailCursor<
  T extends { conversation_id: string; lastMessageAt?: string | null },
>(loaded: T[]): { id: string; lastMessageAt: string | null } | null {
  const tail = loaded[loaded.length - 1];
  if (!tail) return null;
  return { id: tail.conversation_id, lastMessageAt: tail.lastMessageAt ?? null };
}

/**
 * Conversa que não está na lista carregada só entra na janela quando
 * ordena antes do fim já carregado, a página ainda não encheu, ou
 * não há próxima página (a lista local é o conjunto inteiro).
 */
export function shouldPromoteUnloadedConversation(
  window: InboxWindowSnapshot,
  incoming: InboxSortCursor,
): boolean {
  if (window.loadedCount < window.pageSize || !window.hasMore) return true;
  if (!window.tail) return true;
  if (incoming.lastMessageAt === undefined) return false;

  return (
    compareInboxKeysetDesc(
      { id: incoming.id, lastMessageAt: incoming.lastMessageAt },
      window.tail,
    ) < 0
  );
}

export function removeConversationFromLoaded<T extends { conversation_id: string }>(
  loaded: T[],
  conversationId: string,
): { next: T[]; removed: boolean } {
  const next = loaded.filter((row) => row.conversation_id !== conversationId);
  if (next.length === loaded.length) return { next: loaded, removed: false };
  return { next, removed: true };
}

/**
 * Da reconciliação, fica o que já está na lista (atualização) e o que
 * realmente entra na janela. O restante não é fundido.
 */
export function selectDeltaRowsForWindow<
  T extends { conversation_id: string; lastMessageAt?: string | null },
>(loaded: T[], incoming: T[], window: Omit<InboxWindowSnapshot, "loadedCount" | "tail">): T[] {
  const loadedIds = new Set(loaded.map((row) => row.conversation_id));
  const snapshot: InboxWindowSnapshot = {
    loadedCount: loaded.length,
    pageSize: window.pageSize,
    hasMore: window.hasMore,
    tail: inboxTailCursor(loaded),
  };

  return incoming.filter((row) => {
    if (loadedIds.has(row.conversation_id)) return true;
    return shouldPromoteUnloadedConversation(snapshot, {
      id: row.conversation_id,
      lastMessageAt: row.lastMessageAt ?? null,
    });
  });
}

/** Substitui linhas já carregadas e acrescenta só as que passaram no filtro da janela. */
export function mergeLoadedInboxRows<T extends { conversation_id: string }>(
  current: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) return current;
  const byId = new Map(current.map((row) => [row.conversation_id, row]));
  for (const row of incoming) byId.set(row.conversation_id, row);
  return [...byId.values()];
}
